import type { Persona, StudioId } from '@/domain/types';
import { reportingMoneyVisible } from '@/domain/permissions';

/** The active reporting filter, shared by every tab. `studioIds` is the explicit
 *  selection; an empty array means all studios (network-wide). */
export interface ReportingScope {
  days: readonly string[];
  studioIds: readonly StudioId[];
  compareMode: boolean;
  moneyVisible: boolean;
}

export interface StudioSeries<T> {
  studioId: StudioId;
  value: T;
}

/** Turn a studio selection into a per-studio result set for side-by-side
 *  comparison. The single place comparison fan-out lives. */
export function perStudioSeries<T>(
  studioIds: readonly StudioId[],
  compute: (studioId: StudioId) => T,
): StudioSeries<T>[] {
  return studioIds.map((studioId) => ({ studioId, value: compute(studioId) }));
}

/** The `studioId` to pass to `(days, studioId?)` selectors when NOT comparing:
 *  exactly one studio → that id; zero or many → undefined (network / handled by
 *  perStudioSeries). */
export function singleStudioScope(studioIds: readonly StudioId[]): StudioId | undefined {
  return studioIds.length === 1 ? studioIds[0] : undefined;
}

export function buildReportingScope(
  days: readonly string[],
  studioIds: readonly StudioId[],
  persona: Persona | null,
): ReportingScope {
  return {
    days,
    studioIds,
    compareMode: studioIds.length > 1,
    moneyVisible: reportingMoneyVisible(persona),
  };
}
